import React, { useEffect, useRef, useState } from 'react';
import { Box, debounce, FormControlLabel, Tooltip, Stack, Switch, Typography } from '@mui/material';
import { useTheme } from "@mui/material/styles";
import { getPieChartOptions } from './chartWidgetUtility';
import LoaderComponent from 'components/Loader';
import ReactECharts from 'echarts-for-react';
import { ECElementEvent } from 'echarts';
import ToolTipIcon from '../../assets/info-icon.svg';
import { SxProps, Theme } from '@mui/system';


// Define the component props
interface WidgetProps {
	title?: any;
	value: string | number;
	change: number;
	changeType: 'increase' | 'decrease' | string;
	isLoading: boolean;
	isOverlayWidget?: boolean;
	tooltipLabel?: string | JSX.Element | {
		title: string;
		description: string;
	}[];
	data?: any[];
	envNames?: string[];
	seriesData?: any;
	minTitleHeight?: `${number}px`;
	hasTooltip?: boolean;
	handleEvent?: (event: ECElementEvent) => void;
	showDollar?: boolean;
	shortDesc?: string | null;
	customSize?: string
	colorMapping?: any;
	enableSectorCut?: boolean;
	setEnableSectorCut?: any;
	sx?: SxProps<Theme>;
}

const defaultOptions = {
	graphic: [
		{
			type: 'circle',
			left: 'center',
			top: 'center',
			shape: {
				r: 100
			},
			style: {
				fill: '#71758f66'
			}
		}
	],
	title: {
		show: true,
		textStyle: {
			color: '#bcbcbc',
			fontWeight: 'normal',
			fontSize: 16
		},
		text: 'No data',
		left: 'center',
		top: 'center'
	},
	backgroundColor: '#053367'
};

const PieInfoWidget: React.FC<WidgetProps> = ({ title = '', value, change, changeType, isLoading = false,
	isOverlayWidget = false,
	tooltipLabel = null,
	seriesData,
	minTitleHeight = '50.5px',
	hasTooltip=true,
	handleEvent,
	customSize = '450px',
	showDollar = true,
	shortDesc = null,
	colorMapping,
	enableSectorCut,
	setEnableSectorCut,
	sx
}) => {
	const theme = useTheme();
	const colors = theme.palette;
	const chartContainerRef = useRef<HTMLDivElement>(null);
		const [windowSize, setWindowSize] = useState(window.innerWidth);
		const [gridLeft, setGridLeft] = useState('5%');
		const customSizeInt = parseInt(customSize.replace('px',''))
		useEffect(() => {
	
			// re-render chart to fix alignment when resizing the browser
			const handleResize = debounce(() => {
			if (chartContainerRef.current) {
				const containerWidth = chartContainerRef.current.offsetWidth;

				let calculatedLeft = '5%';

				if (containerWidth < 420) {
					calculatedLeft = '18%';
				} else if (containerWidth < 600) {
					calculatedLeft = '15%';
				} else if (containerWidth < 820) {
					calculatedLeft = '12%';
				} else if (containerWidth < 920) {
					calculatedLeft = '9%';
				} else if (containerWidth < 1260) {
					calculatedLeft = '7%';
				}

				setGridLeft(calculatedLeft);
				setWindowSize(window.innerWidth);
			}
		}, 200);
	
			handleResize();
			window.addEventListener('resize', handleResize);
			return () => {
				window.removeEventListener('resize', handleResize);
			};
		}, []);
	
		const result = getPieChartOptions(seriesData,chartContainerRef?.current?.offsetWidth, colorMapping ?? {}, showDollar);
		
		const adjustedOption: any = !seriesData.length
				? defaultOptions
				: {
						...result,
						grid: {
							...result,
							left: gridLeft
						}
				  };

	return (
		<Box
			sx={{
				backgroundColor: '#003771',
				display: 'flex',
				flexDirection: 'row',
				color: 'white',
				padding: '16px',
				borderRadius: '5px',
				height: customSizeInt < 300 ? '16rem' : '26rem',
				...sx
			}}
		>
			{!isLoading ? (
			<>
			{/* Left Section (55%) */}
			<Box
				sx={{
					display: 'flex',
					flexDirection: 'column',
					justifyContent: 'space-around',
					borderRight: `2px solid ${colors.dark.dark}`,
					width: {xs: '130px', sm: '170px', md: '190px'}
				}}
			>
				<Stack direction="row" alignItems="center">
					<Typography variant="widgetTitle">
						{title}
					</Typography>
					{hasTooltip && !!tooltipLabel && (
						<Box
							className="hover-content"
							sx={{
								mr: 1,
								transition: 'opacity 0.3s ease-in-out'
							}}
						>
							<Tooltip
								arrow
								title={
									Array.isArray(tooltipLabel) ? (
									<Box component="ul" sx={{ pl: 2, m: 0 }}>
										{tooltipLabel.map((item, idx) => (
										<li key={`${item.title}-${idx}`}>
											<Typography variant="bodyText">
											<strong>{item.title} : </strong> {item.description}
											</Typography>
										</li>
										))}
									</Box>
									) : (
									tooltipLabel
									)
								}
							>
								<img src={ToolTipIcon} />
							</Tooltip>
						</Box>
					)}
				</Stack>
				<Typography variant='widgetValue'>
					{value}
				</Typography>

				{/* Change Indicator */}
				{/* <Box display="flex" alignItems="center" justifyContent='flex-start' mt={1} color={changeType === 'increase' ? '#D82B51' : '#01C008'}>
					{changeType === 'increase' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />}
					<Typography
						variant="body2"
						sx={{
							fontSize: {
								xs: '10px',
								sm: '12px',
								md: '14px',
								lg: '16px'
							},
							fontWeight: 500,
							ml: 0.5
						}}
					>
						{change}% vs previous month
					</Typography>
				</Box> */}
			</Box>

			<Stack flexGrow={1} justifyContent="center" key={windowSize} ref={chartContainerRef} minHeight={customSize}>
				{!!setEnableSectorCut && <FormControlLabel style={{justifyContent: 'end'}}
					control={<Switch color={!enableSectorCut ? 'warning' : 'info'} onChange={(e) => setEnableSectorCut(e.target.checked)} checked={enableSectorCut} size="small"/>}
					label={'Sector'}
				/>}
				<ReactECharts
					style={{ width: '100%', height: '100%' }}
					option={adjustedOption}
					onEvents={{
						click: (params: ECElementEvent) => {
							if (handleEvent && seriesData.length) {
								handleEvent(params);
							}
						}
					}}
					opts={{ renderer: 'canvas', width: 'auto' }}
					notMerge={true}
					lazyUpdate={true}
				/>
				</Stack>
			</>
			) : (
				<LoaderComponent />
			)}
		</Box>
	);
};

export default PieInfoWidget;
